import React, { useEffect, useMemo, useRef, useState } from 'react';

import { WidgetsMapComponentKeys } from 'config/widgetConfigurations/widgetsConfigurations';
import { Link, useParams } from 'react-router-dom';
import { useSubscription } from 'react-stomp-hooks';
import { getStringWithoutHash } from 'utilitys/helpers/general';

import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { useReactTableState } from 'hooks/useReactTableState';
import useTableCurrentState from 'hooks/useTableCurrentState';
import useUserHook from 'hooks/useUserHook';

import { useVersionStore } from 'store/version.store';

import { useMutationDownloadCsvFile } from 'api/mutations/downloadCsvFile.mutation';
import { useMutationRowsBoardToCsv } from 'api/mutations/extractRowsBoardToCsv.mutation';
import { useMutationRowsBoardToXls } from 'api/mutations/extractRowsBoardToXls.mutation';
import { ISingleSmallCardResponse, useQueryListCardsData } from 'api/queries/getListFilteredCards.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import {
    pptxTableNarrativeHeaders,
    serializedOneLevelDataStructureTableToPPTX,
} from 'components/common/ExportDropdown/exportDropdown.utils';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { serializeColumnsVisibility } from 'components/common/Tables/Tables.utils';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { IRowsBoard } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Button } from 'components/DesignSystem/Button';
import classes from 'components/widgets/ScheduleSimulation/components/ChangedActivitiesTable/ChangedActivitiesTable.module.scss';
import {
    columnsV8,
    initialState,
    linkToBattleCards,
} from 'components/widgets/ScheduleSimulation/components/ChangedActivitiesTable/ChangedActivitiesTableColumns.utils';
import { XlsExportModal } from 'components/widgets/ScheduleSimulation/components/ChangedActivitiesTable/XlsExportModal/XlsExportModal';
import { useSimulationCacheKeyStore } from 'components/widgets/ScheduleSimulation/components/SimulatedActivitiesTable/simulationCacheKey.store';

export interface Props extends Omit<IRowsBoard, 'filterData'> {
    handleRunSimulation: () => void;
}

export const ChangedActivitiesTable = ({
    filter,
    projectId,
    contractId,
    latestVersionId,
    setSubTitleForExport,
    handleRunSimulation,
}: Props) => {
    const [isXlsExportModal, setIsXlsExportModal] = useState<boolean>(false);
    const {
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        rowSelection,
        setRowSelection,
    } = useReactTableState({ initialSorting: [] });
    const { id } = useParams<any>();
    const { email } = useUserHook();
    const { isRunning: isSimulationRunning, resetSimulationSession } = useSimulationCacheKeyStore();
    const currentVersion = useVersionStore((state) => state.version);
    const filterData = useMemo(() => {
        if (projectId && contractId && latestVersionId) {
            return {
                filter: {
                    filters: [
                        {
                            data: [projectId],
                            name: 'PROJECT_CARD_BOARD',
                            operation: null,
                        },
                        {
                            data: [contractId],
                            name: 'CONTRACT_CARD_BOARD',
                            operation: null,
                        },
                        {
                            data: [latestVersionId],
                            name: 'VERSION_CARD_BOARD',
                            operation: null,
                        },
                        {
                            data: ['PROGRAMME_ACTIVITY'],
                            name: 'TYPE_CARD_BOARD',
                            operation: null,
                        },
                        {
                            data: ['INTERIM_CHANGED'],
                            name: 'STATUS_CARD_BOARD',
                            operation: null,
                        },
                    ],
                },
            };
        }
        return {
            filter: {
                filters: [],
            },
        };
    }, [projectId, contractId, latestVersionId]);

    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(-1);
    const { sendEventWithDimensions } = useEventWithDimensions();
    // FOR PinBoard Only
    const route = `${getStringWithoutHash(window.location.href)}`;
    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);

    //  reset Pagination on sorting and new filter request
    useEffect(() => {
        setPagination({ ...pagination, page: 1 });
    }, [sorting]);

    const cardsDataFilters = useMemo(
        () => ({
            cardCachedFilter: filterData,
            paging: { ...pagination, page: pagination.page - 1 },
            sortingRequestList: sorting,
        }),
        [filterData, filter, latestVersionId, pagination, sorting],
    );

    const { data: cards, isLoading, isFetching } = useQueryListCardsData(cardsDataFilters);

    const boundaryRef = useRef<HTMLDivElement>(null);

    const tableData = useMemo(() => {
        if (cards && cards.cardList.length > 0) {
            return cards.cardList.map((card) => {
                return {
                    ...card,
                    className: classes.activityStatusChanged,
                };
            });
        } else {
            return [];
        }
    }, [cards]);

    useEffect(() => {
        if (cards) {
            setTotalPages(cards.numOfPages);
            setTotalCount(cards.numOfAllCards);
        }
    }, [cards]);

    const pptxData = serializedOneLevelDataStructureTableToPPTX({
        data: cards?.cardList,
        sortBy: { key: 'endDate', sortOrder: 'asc' },
        headers: pptxTableNarrativeHeaders,
    });

    const subTitle = useCustomPageSubTitle({ projectId, contractId, latestVersionId });

    const componentKey: WidgetsMapComponentKeys = 'changedActivities';
    const updatedInitialState = useTableCurrentState({ initialState, componentKey });
    const title = 'Updates made since latest upload';

    const { mutate: csvMutate, isLoading: csvMutateLoading } = useMutationRowsBoardToCsv();
    const { mutate: xlsMutate, isLoading: xlsMutateLoading } = useMutationRowsBoardToXls();
    const [isDownloadCsv, setIsDownloadCsv] = useState<boolean>(false);

    const exportIsLoading = isDownloadCsv || csvMutateLoading || xlsMutateLoading;

    const { handleSnackBar } = useCustomSnackBar();
    const { mutate: downloadCsvFile } = useMutationDownloadCsvFile();

    useSubscription(email ? [`/topic/${email}.notifications.push`] : [], (response) => {
        const { type, message } = JSON.parse(response.body);
        if (type === 'CsvFileReady') {
            setIsDownloadCsv(false);
            const { fileName } = message;
            if (fileName) {
                downloadCsvFile({ fileName, downloadName: `${title}_${subTitle}` });
                sendEventWithDimensions({
                    category: 'Export',
                    action: 'CSV',
                    label: `${title} ${subTitle}`,
                });
            }
        }
    });

    const handleAPICsvExport = ({ isCompact = false }: { isCompact?: boolean }) => {
        setIsDownloadCsv(true);
        csvMutate({
            filter: cardsDataFilters.cardCachedFilter,
            fileName: `${title}_${subTitle}`,
            isCompact: isCompact,
        });
    };

    const handleAPIXlsExport = (data) => {
        xlsMutate(
            {
                filter: cardsDataFilters.cardCachedFilter,
                fileName: `${title}_${subTitle}`,
                configRequest: {
                    contractId: data.contractId,
                    isExtractRemainingDuration: data.remainingDurationInDays,
                    isExtractPercentageComplete: data.submittedDurationComplete,
                    userData: data.userData,
                },
            },
            {
                onSuccess: () => {
                    handleSnackBar('File Successfully Downloaded', 'success');

                    sendEventWithDimensions({
                        category: 'Export',
                        action: 'XLS',
                        label: `${title} ${subTitle}`,
                    });
                },
                onError: (error) => {
                    const message = 'Failed To Download File';
                    DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
                },
            },
        );
    };

    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ subTitle, title });
    }, [subTitle, title]);

    const titleWithCount = useMemo(() => {
        if (totalCount === -1) {
            return title;
        }
        return `${title} (${totalCount})`;
    }, [title, totalCount]);

    useEffect(() => {
        updatedInitialState && setColumnOrder(updatedInitialState.columnOrder ?? []);
        updatedInitialState &&
            setColumnVisibility(
                serializeColumnsVisibility({ columns: columnsV8, hiddenColumns: updatedInitialState.hiddenColumns }) ??
                    {},
            );
    }, [updatedInitialState]);

    const tableInstance = useMemo(() => {
        const hiddenColumns = Object.keys(columnVisibility).reduce((acc: string[], item: string) => {
            columnVisibility[item] === false && acc.push(item);
            return acc;
        }, []);

        return {
            setColumnOrder,
            setHiddenColumns: (hiddenColumns) =>
                setColumnVisibility(serializeColumnsVisibility({ columns: columnsV8, hiddenColumns })),
            state: {
                hiddenColumns,
            },
            allColumns: columnOrder
                .map((item) => {
                    const column = columnsV8.find((column) => column.id === item);
                    if (column) {
                        return {
                            ...column,
                            isVisible: column?.id ? !hiddenColumns.includes(column.id) : false,
                        };
                    }
                    return null;
                })
                .filter((x) => x),
        };
    }, [columnVisibility, columnOrder]);

    useEffect(() => {
        resetSimulationSession();
    }, [currentVersion]);

    return (
        <>
            <WidgetWithTitle
                id={componentKey}
                className={classes.changedActivitiesContainer}
                title={<WidgetTitle title={titleWithCount} />}
                titleComponents={[
                    <div
                        key={'linkToProjectActivities'}
                        style={{ alignSelf: 'center' }}
                        className={classes.linkContainer}
                    >
                        <Link to={linkToBattleCards(id)}>View in {executionConfig.battlecards.title}</Link>
                    </div>,
                    <ExportDropdown
                        key={'ExportDropdown'}
                        title={title}
                        pptxData={pptxData}
                        ref={boundaryRef}
                        subTitle={subTitle}
                        handleAPICsvExport={handleAPICsvExport}
                        handleAPICsvExportCompact={() => {
                            handleAPICsvExport({ isCompact: true });
                        }}
                        handleAPIXlsExport={() => setIsXlsExportModal(true)}
                    />,
                    <ColumnsManager
                        onOpenModal={() => setModalColumnsOpen(true)}
                        onHideModal={() => setModalColumnsOpen(false)}
                        showModal={modalColumnsOpen}
                        componentKey={componentKey}
                        tableInstance={tableInstance}
                        initialState={initialState}
                        columns={columnsV8}
                        onChangeOrder={setColumnOrder}
                        onChangeVisibility={setColumnVisibility}
                    />,
                    <ThreeDotsMenu
                        widgetId={undefined}
                        componentKey={componentKey}
                        shareTitle={title}
                        shareLink={route}
                    />,
                ]}
            >
                <div className={classes.rowsBoardWrapper} ref={boundaryRef}>
                    {updatedInitialState && (
                        <TableV8<ISingleSmallCardResponse>
                            data={tableData}
                            columns={columnsV8}
                            isLoading={isLoading || isFetching || exportIsLoading}
                            noData={<NoData>No activities match the applied filter</NoData>}
                            sorting={sorting}
                            setSorting={setSorting}
                            columnVisibility={columnVisibility}
                            setColumnVisibility={setColumnVisibility}
                            columnOrder={columnOrder}
                            setColumnOrder={setColumnOrder}
                            manualPagination={false}
                            rowSelection={rowSelection}
                            setRowSelection={setRowSelection}
                            totalPages={totalPages}
                            pagination={pagination}
                            setPagination={setPagination}
                            isPaginationDisabled={isFetching}
                            displayPaginationBottomBar
                        />
                    )}
                </div>

                <XlsExportModal
                    isXlsExportModal={isXlsExportModal}
                    setIsXlsExportModal={setIsXlsExportModal}
                    handleAPIXlsExport={handleAPIXlsExport}
                />
            </WidgetWithTitle>

            <div className={classes.footer}>
                <Button
                    disabled={!cards || cards.cardList?.length === 0 || isSimulationRunning}
                    isLoading={isSimulationRunning}
                    size={'large'}
                    color={'primary'}
                    onClick={() => {
                        handleRunSimulation();
                    }}
                >
                    Run simulation
                </Button>
            </div>
        </>
    );
};
